import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

export default function AddOfferModal({ Offer, onClose, refreshData }) {
    const [name, setName] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [loading, setLoading] = useState(false);

    // Stan galerii serwera
    const [serverImages, setServerImages] = useState([]);
    const [showServerGallery, setShowServerGallery] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        if (Offer) {
            setName(Offer.name);
            setExistingImages(Offer.images ? JSON.parse(Offer.images) : []);
        }
    }, [Offer]);

    const fetchServerImages = async () => {
        try {
            const res = await axios.get('https://megamont.pl/api/all-images');
            setServerImages(res.data);
            setShowServerGallery(true);
        } catch (err) {
            console.error("Błąd galerii:", err);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);
        const urls = files.map(file => URL.createObjectURL(file));
        setPreviewUrls(urls);
    };

    const filteredImages = useMemo(() => {
        return serverImages.filter(img => img.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [serverImages, searchTerm]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentImages = filteredImages.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredImages.length / itemsPerPage);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData();
        formData.append('name', name);
        selectedFiles.forEach(file => formData.append('images', file));
        formData.append('existingImages', JSON.stringify(existingImages));

        try {
            if (Offer) {
                await axios.put(`https://megamont.pl/api/offers/${Offer.id}`, formData);
            } else {
                await axios.post('https://megamont.pl/api/offers', formData);
            }
            refreshData();
            onClose();
        } catch (err) {
            alert("Błąd zapisu.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="custom-modal-backdrop">
            <div className="custom-modal-window shadow-lg">
                
                {/* Fixed Header */}
                <div className="custom-modal-header bg-primary text-white">
                    <h4 className="m-0 fw-bold">{Offer ? '🔧 Edycja Oferty' : '✨ Nowa Oferta'}</h4>
                    <button onClick={onClose} className="btn-close btn-close-white"></button>
                </div>

                {/* Scrollable Content */}
                <div className="custom-modal-body">
                    <form onSubmit={handleSubmit} id="offerForm">
                        <div className="mb-4">
                            <label className="form-label fw-bold text-secondary small">NAZWA OFERTY</label>
                            <input 
                                type="text" 
                                className="form-control form-control-lg border-2" 
                                value={name} 
                                onChange={(e) => setName(e.target.value)} 
                                required 
                                placeholder="Np. Konstrukcje stalowe..." 
                            />
                        </div>

                        <div className="d-flex gap-3 mb-4">
                            <label className="btn btn-dark px-4 py-2 shadow-sm">
                                📁 Wybierz z komputera
                                <input type="file" hidden multiple accept="image/*" onChange={handleFileChange} />
                            </label>
                            <button type="button" className="btn btn-outline-primary px-4 py-2 shadow-sm" onClick={fetchServerImages}>
                                🌐 Galeria Serwera
                            </button>
                        </div>

                        {/* Serwer Gallery Section */}
                        {showServerGallery && (
                            <div className="server-gallery-box p-3 mb-4 rounded-3 border bg-light shadow-sm">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="m-0 fw-bold">Pliki na serwerze</h6>
                                    <input 
                                        type="text" 
                                        className="form-control form-control-sm w-50" 
                                        placeholder="Szukaj pliku..." 
                                        onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}} 
                                    />
                                </div>
                                
                                <div className="server-grid mb-3">
                                    {currentImages.map((path, i) => (
                                        <div key={i} className="server-img-item" onClick={() => setExistingImages(prev => [...new Set([...prev, path])])}>
                                            <img src={`https://megamont.pl/uploads/${path}`} alt="srv" />
                                            <div className="img-overlay"><span>DODAJ</span></div>
                                        </div>
                                    ))}
                                </div>

                                <div className="d-flex justify-content-between align-items-center border-top pt-2">
                                    <div className="btn-group">
                                        <button type="button" className="btn btn-sm btn-outline-dark" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Wstecz</button>
                                        <button type="button" className="btn btn-sm btn-outline-dark" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Dalej</button>
                                    </div>
                                    <small className="text-muted">Strona {currentPage} z {totalPages}</small>
                                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setShowServerGallery(false)}>Zamknij</button>
                                </div>
                            </div>
                        )}

                        {/* Current Selection Preview */}
                        <div className="mb-4">
                            <label className="form-label fw-bold text-secondary small">ZDJĘCIA W OFERCIE ({existingImages.length + selectedFiles.length})</label>
                            <div className="image-preview-container p-3 border rounded-3 bg-white">
                                {existingImages.length === 0 && selectedFiles.length === 0 && (
                                    <div className="text-center text-muted py-4">Brak wybranych zdjęć</div>
                                )}
                                
                                <div className="d-flex flex-wrap gap-3">
                                    {/* Existing / Server Images */}
                                    {existingImages.map((img, idx) => (
                                        <div key={idx} className="preview-item">
                                            <img src={img.includes('/') ? `https://megamont.pl/uploads/${img}` : `https://megamont.pl/uploads/oferta_${Offer?.id}/${img}`} alt="existing" />
                                            <button type="button" className="remove-btn" onClick={() => setExistingImages(existingImages.filter(i => i !== img))}>&times;</button>
                                        </div>
                                    ))}
                                    
                                    {/* Fresh local files */}
                                    {previewUrls.map((url, idx) => (
                                        <div key={idx} className="preview-item new-file">
                                            <img src={url} alt="new" />
                                            <div className="new-badge">NOWE</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Fixed Footer */}
                <div className="custom-modal-footer bg-white border-top p-3 d-flex justify-content-end gap-3">
                    <button type="button" className="btn btn-light border px-4" onClick={onClose} disabled={loading}>Anuluj</button>
                    <button type="submit" form="offerForm" className="btn btn-primary px-5 shadow" disabled={loading}>
                        {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : '💾 Zapisz Ofertę'}
                    </button>
                </div>
            </div>

            <style>{`
                .custom-modal-backdrop {
                    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
                    background: rgba(0,0,0,0.8); z-index: 2000;
                    display: flex; align-items: center; justify-content: center; padding: 20px;
                }
                .custom-modal-window {
                    background: #f8f9fa; width: 100%; max-width: 850px;
                    max-height: 90vh; border-radius: 15px; display: flex; flex-direction: column; overflow: hidden;
                }
                .custom-modal-header { padding: 20px; flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; }
                .custom-modal-body { padding: 25px; overflow-y: auto; flex-grow: 1; }
                .custom-modal-footer { flex-shrink: 0; }

                .server-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
                .server-img-item { position: relative; height: 80px; border-radius: 8px; overflow: hidden; cursor: pointer; border: 1px solid #ddd; }
                .server-img-item img { width: 100%; height: 100%; object-fit: cover; }
                .img-overlay { 
                    position: absolute; top:0; left:0; width:100%; height:100%; background: rgba(13,110,253,0.7);
                    display: flex; align-items: center; justify-content: center; color: white; opacity: 0; transition: 0.2s; font-size: 12px; font-weight: bold;
                }
                .server-img-item:hover .img-overlay { opacity: 1; }

                .image-preview-container { min-height: 120px; border: 2px dashed #ccc !important; }
                .preview-item { position: relative; width: 100px; height: 100px; border-radius: 10px; border: 1px solid #eee; overflow: visible; }
                .preview-item img { width: 100%; height: 100%; object-fit: cover; border-radius: 10px; }
                .remove-btn { 
                    position: absolute; top: -10px; right: -10px; background: #dc3545; color: white; border: none;
                    width: 25px; height: 25px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; cursor: pointer;
                }
                .new-file { border: 2px solid #0d6efd; }
                .new-badge { 
                    position: absolute; bottom: 0; left: 0; width: 100%; background: #0d6efd; color: white; 
                    font-size: 9px; text-align: center; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;
                }
                .btn-close-white { filter: invert(1); }
            `}</style>
        </div>
    );
}